import React, { useEffect, useState } from 'react';
import API from '../../api';
import { BookOpen, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';

export default function PrezzarioSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const load = () => {
    API.get('/api/settings/prezzario').then(r => setSettings(r.data)).catch(() => setSettings(null));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setSavedMsg('');
    try {
      const r = await API.put('/api/settings/prezzario', {
        default_catalogo: settings.default_catalogo,
        enable_regionale: settings.enable_regionale,
        enable_aziendale: settings.enable_aziendale,
      });
      setSettings(r.data);
      setSavedMsg('Impostazioni salvate. Le pagine Preventivi/Computi/Prezzario useranno il nuovo default.');
      setTimeout(() => setSavedMsg(''), 5000);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-slate-400 text-sm">Caricamento impostazioni...</div>;

  return (
    <div data-testid="prezzario-settings-tab" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen size={22} className="text-primary" />
        <h2 className="text-xl font-bold">Impostazioni Prezzario</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Per default <b>Prezzario CER 2025</b> e' utilizzato ovunque (Preventivi, Computi, Imports).
        Il <b>Prezzario Regionale (Lombardia)</b> e' visibile e selezionabile solo se lo abiliti qui sotto.
      </p>

      <div className="space-y-3">
        <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-md bg-white cursor-pointer hover:border-primary transition-all">
          <input
            type="checkbox"
            data-testid="toggle-enable-regionale"
            checked={!!settings.enable_regionale}
            onChange={e => setSettings({ ...settings, enable_regionale: e.target.checked })}
            className="mt-1 w-5 h-5 accent-primary"
          />
          <div className="flex-1">
            <p className="font-bold">Abilita Prezzario Regionale (Lombardia 2025)</p>
            <p className="text-xs text-slate-500 mt-1">
              Se disattivato (consigliato), il prezzario Lombardia non sara' selezionabile.
              Se attivato, comparira' come opzione accanto al CER nelle pagine Preventivi/Computi/Prezzario.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-md bg-white cursor-pointer hover:border-primary transition-all">
          <input
            type="checkbox"
            data-testid="toggle-enable-aziendale"
            checked={!!settings.enable_aziendale}
            onChange={e => setSettings({ ...settings, enable_aziendale: e.target.checked })}
            className="mt-1 w-5 h-5 accent-primary"
          />
          <div className="flex-1">
            <p className="font-bold">Abilita Listino Aziendale</p>
            <p className="text-xs text-slate-500 mt-1">
              Listino personalizzato dell'impresa (voci AZ-T). Se disattivato non comparira' come scorciatoia di catalogo.
            </p>
          </div>
        </label>

        <div className="p-4 border border-slate-200 rounded-md bg-white">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Catalogo Default</p>
          <select
            data-testid="select-default-catalogo"
            value={settings.default_catalogo}
            onChange={e => setSettings({ ...settings, default_catalogo: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
          >
            <option value="cer_2025">CER 2025 (consigliato)</option>
            <option value="lombardia_2025" disabled={!settings.enable_regionale}>Regionale Lombardia 2025</option>
            <option value="aziendale" disabled={!settings.enable_aziendale}>Listino Aziendale</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1">
            Catalogo usato quando la richiesta non specifica un prezzario (es. AI generate, import, ricerca semantica).
          </p>
        </div>
      </div>

      {!settings.enable_regionale && (
        <div className="mt-5 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <span>Il prezzario Regionale Lombardia non sara' visibile finche' non lo abiliti. Tutte le ricerche e generazioni AI useranno il CER 2025.</span>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-prezzario-settings"
          className="px-5 py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
        >
          <Save size={16} /> {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
        </button>
        {savedMsg && (
          <div className="flex items-center gap-1 text-sm text-green-700">
            <CheckCircle2 size={16} /> {savedMsg}
          </div>
        )}
      </div>
    </div>
  );
}
